import React, { useState, useRef, useEffect } from 'react';
import './SearchableDropdown.css';

/**
 * Searchable Dropdown Component
 * Reusable dropdown with search functionality
 */
function SearchableDropdown({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  disabled = false,
  required = false,
  error = '',
  displayKey = 'name',
  valueKey = 'value',
  searchKeys = null,
  renderOption = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Find selected option for display
  const selectedOption = options.find(opt => opt[valueKey] === value);

  // Filter options based on search (search across multiple keys if provided)
  const filteredOptions = options.filter(opt => {
    const term = searchTerm.toLowerCase();
    if (searchKeys && searchKeys.length > 0) {
      return searchKeys.some(key =>
        opt[key] && opt[key].toString().toLowerCase().includes(term)
      );
    }
    return opt[displayKey].toLowerCase().includes(term);
  });

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleSelect = (option) => {
    onChange(option[valueKey], option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className={`searchable-dropdown ${disabled ? 'disabled' : ''} ${error ? 'has-error' : ''}`} ref={dropdownRef}>
      {label && (
        <label className="dropdown-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      <div
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        <span className={`dropdown-value ${!selectedOption ? 'placeholder' : ''}`}>
          {selectedOption ? (
            renderOption ? renderOption(selectedOption, true) : selectedOption[displayKey]
          ) : placeholder}
        </span>
        <span className="dropdown-arrow">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className="dropdown-menu">
          <div className="dropdown-search">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              className="search-input"
            />
          </div>

          <div className="dropdown-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option[valueKey] || index}
                  className={`dropdown-option ${option[valueKey] === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option)}
                >
                  {renderOption ? renderOption(option, false) : option[displayKey]}
                </div>
              ))
            ) : (
              <div className="dropdown-no-results">No results found</div>
            )}
          </div>
        </div>
      )}

      {error && <span className="dropdown-error">{error}</span>}
    </div>
  );
}

export default SearchableDropdown;
